import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { api, getToken, API, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { getCashAdvanceSummary, getReimbursementSummary, isCashAdvanceInPeriod } from "@/lib/financialSummary";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, FloppyDisk, Image as ImageIcon, DownloadSimple, PencilSimple, LockKey, CheckCircle, Clock } from "@phosphor-icons/react";
import { ReimbursementDetailModal, KasbonDetailModal } from "@/components/DetailModals";
import { CashAdvanceStatusPill } from "@/components/PaymentStatusPill";
import { DeleteConfirmButton } from "@/components/DeleteConfirmButton";
import { CurrencyInput, PercentInput, MonthYearPicker } from "@/components/inputs";
import { useAuth } from "@/context/AuthContext";

export default function EmployeeDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);

  useEffect(() => {
    if (user?.email?.toLowerCase() === "admin2@samitragroup.com") {
      navigate("/admin/attendance", { replace: true });
    }
  }, [user]);
  const [periodPayroll, setPeriodPayroll] = useState(null);
  const [closedPeriods, setClosedPeriods] = useState([]);
  const [period, setPeriod] = useState(() => localStorage.getItem("adminPeriod") || `${new Date().getMonth() + 1}-${new Date().getFullYear()}`);
  const [selMonth, selYear] = period.split("-").map(Number);
  const periodLocked = closedPeriods.some((c) => c.month === selMonth && c.year === selYear);
  const periodLabel = new Date(selYear, selMonth - 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const changePeriod = (m, y) => {
    const p = `${m}-${y}`;
    setPeriod(p);
    localStorage.setItem("adminPeriod", p);
  };
  const yearOptions = [];
  for (let y = new Date().getFullYear() - 2; y <= new Date().getFullYear() + 1; y++) yearOptions.push(y);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", position: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const loadEmp = () => api.get(`/employees/${id}`).then((res) => setEmp(res.data));
  const loadPeriodPayroll = () => api.get(`/payroll/${id}/period?month=${selMonth}&year=${selYear}`).then((res) => setPeriodPayroll(res.data)).catch(() => {});
  useEffect(() => { loadEmp(); }, [id]);
  useEffect(() => { api.get("/payroll/closed-periods").then((res) => setClosedPeriods(res.data)).catch(() => {}); }, []);
  useEffect(() => { loadPeriodPayroll(); }, [id, selMonth, selYear]);

  const openEdit = () => {
    setForm({ name: emp.name || "", position: emp.position || "", email: emp.email || "", password: "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nama dan email wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/employees/${id}`, {
        name: form.name.trim(),
        position: form.position.trim(),
        email: form.email.trim(),
        ...(form.password ? { password: form.password } : {}),
      });
      toast.success("Data karyawan diperbarui");
      setEditOpen(false);
      loadEmp();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteEmployee = async () => {
    await api.delete(`/employees/${id}`);
    toast.success("Karyawan dihapus");
    navigate("/admin");
  };

  const downloadPayslip = async () => {
    try {
      const res = await api.get(`/payroll/${id}/payslip?month=${selMonth}&year=${selYear}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Gagal mengunduh slip gaji");
    }
  };

  if (!emp) return <AppLayout title="Detail Karyawan"><p className="text-muted-foreground">Memuat…</p></AppLayout>;

  return (
    <AppLayout title="Detail Karyawan">
      <div className="max-w-5xl space-y-6 animate-fade-up">
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" className="gap-2" onClick={() => navigate("/admin")} data-testid="back-button">
            <ArrowLeft size={18} /> Kembali
          </Button>
          <div className="flex items-center gap-2">
          <div className="w-56">
            <MonthYearPicker
              month={selMonth}
              year={selYear}
              onChange={changePeriod}
              years={yearOptions}
              monthTestId="detail-period-month"
              yearTestId="detail-period-year"
            />
          </div>
          <Button variant="outline" className="gap-2" onClick={openEdit} data-testid="edit-employee-button">
            <PencilSimple size={16} /> Edit Karyawan
          </Button>
          <Button variant="outline" className="gap-2" onClick={downloadPayslip} data-testid="admin-download-payslip-button">
            <DownloadSimple size={16} /> Slip Gaji
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="gap-2" data-testid="delete-employee-button">
                <Trash size={16} /> Hapus Karyawan
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Hapus {emp.name}?</AlertDialogTitle>
                <AlertDialogDescription>Semua data gaji, reimbursement & kasbon akan terhapus permanen.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Batal</AlertDialogCancel>
                <AlertDialogAction variant="destructive" onClick={deleteEmployee}>Hapus</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </div>
        </div>

        <Card className="p-6 flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-secondary flex items-center justify-center text-xl font-bold uppercase">{emp.name[0]}</div>
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">{emp.name}</h2>
            <p className="text-muted-foreground text-sm">{emp.position || "—"} · {emp.email}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-sm text-muted-foreground">Gaji Bersih</p>
            <p className="font-heading text-xl font-bold text-primary tabular-nums">{formatRupiah(periodPayroll?.net_salary ?? emp.payroll?.net_salary)}</p>
          </div>
        </Card>

        <Tabs defaultValue="payroll">
          <TabsList>
            <TabsTrigger value="payroll" data-testid="tab-payroll">Gaji</TabsTrigger>
            <TabsTrigger value="reimbursement" data-testid="tab-reimbursement">Reimbursement</TabsTrigger>
            <TabsTrigger value="kasbon" data-testid="tab-kasbon">Kasbon</TabsTrigger>
          </TabsList>
          <TabsContent value="payroll"><PayrollEditor userId={id} onSaved={loadEmp} locked={periodLocked} periodLabel={periodLabel} selMonth={selMonth} selYear={selYear} periodPayroll={periodPayroll} onPeriodRefresh={loadPeriodPayroll} /></TabsContent>
          <TabsContent value="reimbursement"><ReimbursementManager userId={id} locked={periodLocked} selMonth={selMonth} selYear={selYear} /></TabsContent>
          <TabsContent value="kasbon"><KasbonManager userId={id} locked={periodLocked} closedPeriods={closedPeriods} selMonth={selMonth} selYear={selYear} /></TabsContent>
        </Tabs>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Data Karyawan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nama</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" data-testid="edit-employee-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Jabatan</Label>
              <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Contoh: Staff Operasional" data-testid="edit-employee-position" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@samitragroup.com" data-testid="edit-employee-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password Baru</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Kosongkan jika tidak diubah" data-testid="edit-employee-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Batal</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-employee-button">
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function PayrollEditor({ userId, onSaved, locked, periodLabel, selMonth, selYear, periodPayroll, onPeriodRefresh }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (periodPayroll) setP({ ...periodPayroll }); }, [periodPayroll]);

  const setField = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const DEDUCTIONS = [
    { key: "pph21", label: "PPH 21" },
    { key: "bpjs", label: "BPJS TK" },
    { key: "bpjs_kes", label: "BPJS Kesehatan" },
  ];

  const autoCalc = (base, pct) => (Number(base || 0) * Number(pct || 0)) / 100;

  const onBase = (value) => {
    const next = { ...p, base_salary: Number(value) };
    DEDUCTIONS.forEach((d) => {
      next[`${d.key}_amount`] = autoCalc(value, p[`${d.key}_percent`]);
    });
    setP(next);
  };

  const onPercent = (key, value) => {
    setP({ ...p, [`${key}_percent`]: Number(value), [`${key}_amount`]: autoCalc(p.base_salary, value) });
  };

  const onAmount = (key, value) => setP({ ...p, [`${key}_amount`]: Number(value) });

  const addVar = () => setP({ ...p, variable_components: [...(p.variable_components || []), { name: "", amount: 0 }] });
  const removeVar = (i) => setP({ ...p, variable_components: p.variable_components.filter((_, idx) => idx !== i) });
  const setVar = (i, k, v) => {
    const arr = [...p.variable_components];
    arr[i] = { ...arr[i], [k]: k === "amount" ? Number(v) : v };
    setP({ ...p, variable_components: arr });
  };

  const addDed = () => setP({ ...p, deduction_components: [...(p.deduction_components || []), { name: "", amount: 0 }] });
  const removeDed = (i) => setP({ ...p, deduction_components: p.deduction_components.filter((_, idx) => idx !== i) });
  const setDed = (i, k, v) => {
    const arr = [...(p.deduction_components || [])];
    arr[i] = { ...arr[i], [k]: k === "amount" ? Number(v) : v };
    setP({ ...p, deduction_components: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/payroll/${userId}?month=${selMonth}&year=${selYear}`, {
        base_salary: Number(p.base_salary),
        pph21_percent: Number(p.pph21_percent),
        pph21_amount: Number(p.pph21_amount),
        bpjs_percent: Number(p.bpjs_percent),
        bpjs_amount: Number(p.bpjs_amount),
        bpjs_kes_percent: Number(p.bpjs_kes_percent),
        bpjs_kes_amount: Number(p.bpjs_kes_amount),
        variable_components: (p.variable_components || []).map((v) => ({ name: v.name, amount: Number(v.amount) })),
        deduction_components: (p.deduction_components || []).map((v) => ({ name: v.name, amount: Number(v.amount) })),
      });
      toast.success("Data gaji disimpan");
      onSaved();
      onPeriodRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!p) return <p className="text-muted-foreground py-6">Memuat…</p>;
  const variableTotal = (p.variable_components || []).reduce((s, v) => s + Number(v.amount || 0), 0);
  const deductionTotal = (p.deduction_components || []).reduce((s, v) => s + Number(v.amount || 0), 0);
  const net = Number(p.base_salary || 0) + variableTotal - Number(p.pph21_amount || 0) - Number(p.bpjs_amount || 0) - Number(p.bpjs_kes_amount || 0) - deductionTotal;

  return (
    <div className="space-y-6 pt-4">
      {locked && (
        <Card className="p-4 border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10">
          <p className="text-sm text-[hsl(var(--warning))] font-medium flex items-center gap-2">
            <LockKey size={16} weight="bold" /> Periode {periodLabel} sudah ditutup. Data gaji, kasbon, dan reimbursement periode ini tidak bisa diubah sampai buku dibuka kembali dengan PIN.
          </p>
        </Card>
      )}
      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-bold">Gaji Pokok</h3>
        <div className="space-y-1.5">
          <Label>Gaji Pokok (Rp)</Label>
          <CurrencyInput value={p.base_salary} onChange={(num) => onBase(num)} data-testid="payroll-base-input" />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-bold">Komponen Tetap</h3>
        <div className="space-y-4">
          {DEDUCTIONS.map((d) => (
            <div key={d.key} className="space-y-1.5">
              <Label>{d.label}</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <PercentInput value={p[`${d.key}_percent`]} onChange={(num) => onPercent(d.key, num)} data-testid={`payroll-${d.key}-percent`} className="pr-8" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">Rp</span>
                  <CurrencyInput value={p[`${d.key}_amount`]} onChange={(num) => onAmount(d.key, num)} data-testid={`payroll-${d.key}-amount`} className="pl-9" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <span className="text-sm text-muted-foreground">Total Potongan Tetap</span>
          <span className="text-sm font-semibold text-destructive tabular-nums">- {formatRupiah(Number(p.pph21_amount || 0) + Number(p.bpjs_amount || 0) + Number(p.bpjs_kes_amount || 0))}</span>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">Potongan</h3>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={addDed} data-testid="add-deduction-button">
            <Plus size={16} /> Tambah
          </Button>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">Komponen di bawah ini mengurangi gaji bersih karyawan.</p>
        {(p.deduction_components || []).length === 0 && <p className="text-sm text-muted-foreground">Belum ada potongan.</p>}
        {(p.deduction_components || []).map((v, i) => (
          <div key={i} className="flex items-end gap-2" data-testid={`deduction-row-${i}`}>
            <div className="flex-1 space-y-1.5">
              <Label>Nama Potongan</Label>
              <Input value={v.name} onChange={(e) => setDed(i, "name", e.target.value)} placeholder="Potongan..." data-testid={`deduction-name-${i}`} />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label>Nominal (Rp)</Label>
              <CurrencyInput value={v.amount} onChange={(num) => setDed(i, "amount", num)} data-testid={`deduction-amount-${i}`} />
            </div>
            <DeleteConfirmButton
              title="Hapus potongan?"
              description="Komponen potongan ini akan dihapus sebelum data gaji disimpan."
              onConfirm={() => removeDed(i)}
              triggerTestId={`remove-deduction-${i}`}
              confirmTestId={`confirm-remove-deduction-${i}`}
            />
          </div>
        ))}
        {(p.deduction_components || []).length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-sm text-muted-foreground">Total Potongan Tambahan</span>
            <span className="text-sm font-semibold text-destructive tabular-nums">- {formatRupiah(deductionTotal)}</span>
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t-2 border-foreground">
          <span className="text-sm font-bold">Total Potongan</span>
          <span className="text-sm font-bold text-destructive tabular-nums" data-testid="payroll-total-deduction">- {formatRupiah(Number(p.pph21_amount || 0) + Number(p.bpjs_amount || 0) + Number(p.bpjs_kes_amount || 0) + deductionTotal)}</span>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">Komponen Variable</h3>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={addVar} data-testid="add-variable-button">
            <Plus size={16} /> Tambah
          </Button>
        </div>
        {(p.variable_components || []).length === 0 && <p className="text-sm text-muted-foreground">Belum ada komponen variable.</p>}
        {(p.variable_components || []).map((v, i) => (
          <div key={i} className="flex items-end gap-2" data-testid={`variable-row-${i}`}>
            <div className="flex-1 space-y-1.5">
              <Label>Nama Komponen</Label>
              <Input value={v.name} onChange={(e) => setVar(i, "name", e.target.value)} placeholder="Tunjangan..." data-testid={`variable-name-${i}`} />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label>Nominal (Rp)</Label>
              <CurrencyInput value={v.amount} onChange={(num) => setVar(i, "amount", num)} data-testid={`variable-amount-${i}`} />
            </div>
            <DeleteConfirmButton
              title="Hapus komponen variable?"
              description="Komponen variable ini akan dihapus sebelum data gaji disimpan."
              onConfirm={() => removeVar(i)}
              triggerTestId={`remove-variable-${i}`}
              confirmTestId={`confirm-remove-variable-${i}`}
            />
          </div>
        ))}
        {(p.variable_components || []).length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-sm text-muted-foreground">Total Komponen Variable</span>
            <span className="text-sm font-semibold text-[hsl(var(--success))] tabular-nums">+ {formatRupiah(variableTotal)}</span>
          </div>
        )}
      </Card>

      <Card className="p-6 border-l-4 border-l-primary flex items-center justify-between">
        <div>
          <p className="font-heading font-bold">Gaji Bersih (Preview)</p>
          <p className="text-xs text-muted-foreground mt-0.5">Pokok + variable − seluruh potongan</p>
        </div>
        <span className="font-heading text-2xl font-bold tabular-nums" data-testid="payroll-net-preview">{formatRupiah(net)}</span>
      </Card>

      <Button onClick={save} disabled={saving || locked} className="gap-2" data-testid="save-payroll-button">
        <FloppyDisk size={18} /> {saving ? "Menyimpan…" : locked ? "Periode Ditutup" : "Simpan Perubahan Gaji"}
      </Button>
    </div>
  );
}

function ReimbursementManager({ userId, locked, selMonth, selYear }) {
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () => api.get(`/reimbursements?user_id=${userId}`).then((res) => {
    setItems(res.data);
    setDetail((prev) => (prev ? res.data.find((x) => x.id === prev.id) || null : prev));
  });
  useEffect(() => { load(); }, [userId, selMonth, selYear]);

  const periodItems = items.filter((r) => {
    if (Number(r.month) === Number(selMonth) && Number(r.year) === Number(selYear)) return true;

    const parts = String(r.date || "").split(/[/-]/).map(Number);
    const [dayOrYear, month, yearOrDay] = parts;
    const year = String(r.date || "").includes("-") ? dayOrYear : yearOrDay;
    return Number(month) === Number(selMonth) && Number(year) === Number(selYear);
  });
  const summary = getReimbursementSummary(periodItems);

  const toggleStatus = async (r) => {
    const res = await api.put(`/reimbursements/${r.id}`, { status: r.status === "paid" ? "unpaid" : "paid" });
    if (res.data?.email_sent === false) {
      toast.warning("Status diperbarui. Email notifikasi gagal terkirim (email karyawan mungkin tidak valid).");
    } else if (res.data?.email_sent === true) {
      toast.success("Ditandai Paid & email notifikasi terkirim ke karyawan.");
    } else {
      toast.success("Status diperbarui");
    }
    load();
  };
  const remove = async (id) => { await api.delete(`/reimbursements/${id}`); toast.success("Dihapus"); load(); };

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="p-5">
          <p className="text-sm font-medium text-muted-foreground">Total Klaim</p>
          <p className="mt-1 font-heading text-2xl font-bold tabular-nums" data-testid="admin-reimbursement-total">{formatRupiah(summary.total)}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm font-medium text-muted-foreground">Sudah Dibayar</p>
          <p className="mt-1 font-heading text-2xl font-bold tabular-nums text-[hsl(var(--success))]" data-testid="admin-reimbursement-paid">{formatRupiah(summary.paid)}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm font-medium text-muted-foreground">Belum Dibayar</p>
          <p className="mt-1 font-heading text-2xl font-bold tabular-nums text-[hsl(var(--warning))]" data-testid="admin-reimbursement-unpaid">{formatRupiah(summary.unpaid)}</p>
        </Card>
      </div>
      <Card className="overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">No</TableHead>
              <TableHead>Tanggal</TableHead>
              <TableHead>Keterangan</TableHead>
              <TableHead className="text-right">Jumlah</TableHead>
              <TableHead>Bon</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {periodItems.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Belum ada data pada periode ini.</TableCell></TableRow>
            ) : periodItems.map((r, idx) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetail(r)} data-testid={`admin-reimbursement-row-${idx}`}>
                <TableCell>{r.no}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell className="max-w-[200px] truncate">{r.description}</TableCell>
                <TableCell className="text-right tabular-nums">{formatRupiah(r.amount)}</TableCell>
                <TableCell>
                  {r.photo_path ? (
                    <Button variant="link" size="sm" onClick={(e) => { e.stopPropagation(); setPreview(`${API}/files/${r.photo_path}?auth=${getToken()}`); }} aria-label={`Lihat foto bon reimbursement ${r.no || ""}`}>
                      <ImageIcon size={16} /> Lihat
                    </Button>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </TableCell>
                <TableCell>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant={r.status === "paid" ? "success" : "warning"}
                        size="sm"
                        shape="pill"
                        onClick={(e) => e.stopPropagation()}
                        disabled={locked}
                        title={locked ? "Period is closed" : r.status === "paid" ? "Mark as unpaid" : "Mark as paid"}
                        aria-label={r.status === "paid" ? "Paid status, mark as unpaid" : "Unpaid status, mark as paid"}
                        data-testid={`toggle-status-${idx}`}
                      >
                        {r.status === "paid" ? <CheckCircle size={15} weight="fill" /> : <Clock size={15} weight="bold" />}
                        {r.status === "paid" ? "Paid" : "Unpaid"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {r.status === "paid" ? "Tandai sebagai Belum Dibayar?" : "Apakah reimbursement ini sudah dibayar?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {r.status === "paid"
                            ? `Reimbursement ${r.no || ""} akan diubah menjadi Unpaid.`
                            : `Reimbursement ${r.no || ""} akan ditandai Paid dan email notifikasi dikirim ke karyawan.`}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction variant={r.status === "paid" ? "warning" : "success"} onClick={() => toggleStatus(r)} data-testid={`confirm-toggle-status-${idx}`}>
                          {r.status === "paid" ? "Ya, Batalkan" : "Ya, Sudah Dibayar"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
                <TableCell>
                  <DeleteConfirmButton
                    title="Hapus reimbursement?"
                    description="Data reimbursement dan foto bon akan dihapus permanen."
                    onConfirm={() => remove(r.id)}
                    triggerTestId={`delete-admin-reimbursement-${idx}`}
                    confirmTestId={`confirm-delete-admin-reimbursement-${idx}`}
                    stopPropagation
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-muted-foreground mt-3">Klik baris untuk detail, atau klik badge status untuk menandai Paid/Unpaid.</p>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Foto Bon</DialogTitle></DialogHeader>
          {preview && <img src={preview} alt="Bon" className="w-full max-h-[70vh] rounded-lg border border-border bg-muted object-contain" />}
        </DialogContent>
      </Dialog>
      <ReimbursementDetailModal item={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function KasbonManager({ userId, locked, closedPeriods = [], selMonth, selYear }) {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get(`/cashadvances?user_id=${userId}`).then((res) => setItems(res.data));
  useEffect(() => { load(); }, [userId, selMonth, selYear]);

  const periodItems = items.filter((c) => isCashAdvanceInPeriod(c, selMonth, selYear));
  const summary = getCashAdvanceSummary(items, selMonth, selYear);

  const setStatus = async (c, status) => {
    const res = await api.put(`/cashadvances/${c.id}?month=${selMonth}&year=${selYear}`, { status });
    if (res.data?.email_sent === false) {
      toast.warning("Status diperbarui. Email notifikasi gagal terkirim (email karyawan mungkin tidak valid).");
    } else if (res.data?.email_sent === true) {
      toast.success("Status diperbarui & email notifikasi terkirim ke karyawan.");
    } else {
      toast.success("Status diperbarui");
    }
    setEditingId(null);
    load();
  };
  const remove = async (id) => { await api.delete(`/cashadvances/${id}`); toast.success("Dihapus"); load(); };

  const toggleInstallment = async (idx, paid) => {
    try {
      const res = await api.put(`/cashadvances/${detail.id}/installment/${idx}`, { paid });
      setDetail(res.data);
      toast.success(paid ? "Cicilan ditandai sudah dipotong" : "Tanda potong dibatalkan");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="p-5">
          <p className="text-sm font-medium text-muted-foreground">Total Kasbon Keseluruhan</p>
          <p className="mt-1 font-heading text-2xl font-bold tabular-nums" data-testid="admin-cashadvance-total">{formatRupiah(summary.total)}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm font-medium text-muted-foreground">Potongan Periode Ini</p>
          <p className="mt-1 font-heading text-2xl font-bold tabular-nums text-destructive" data-testid="admin-cashadvance-current-period-deduction">{formatRupiah(summary.currentPeriodDeduction)}</p>
        </Card>
      </div>
      <Card className="overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14">No</TableHead>
              <TableHead>Keterangan</TableHead>
              <TableHead className="text-center">Cicilan</TableHead>
              <TableHead className="text-right">Per Bulan</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {periodItems.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Belum ada data pada periode ini.</TableCell></TableRow>
            ) : periodItems.map((c, idx) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => setDetail(c)} data-testid={`admin-kasbon-row-${idx}`}>
                <TableCell>{c.no}</TableCell>
                <TableCell className="max-w-[200px] truncate">{c.description}</TableCell>
                <TableCell className="text-center">{c.installments}x</TableCell>
                <TableCell className="text-right tabular-nums">{formatRupiah(c.monthly_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatRupiah(c.total_amount)}</TableCell>
                <TableCell><CashAdvanceStatusPill status={c.status} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {c.status === "pending" || editingId === c.id ? (
                      <>
                        <Button variant="success" size="sm" shape="pill" disabled={locked} title={locked ? "Period is closed" : undefined} onClick={() => setStatus(c, "approved")} data-testid={`approve-kasbon-${idx}`}>Approve</Button>
                        <Button variant="destructive" size="sm" shape="pill" disabled={locked} title={locked ? "Period is closed" : undefined} onClick={() => setStatus(c, "rejected")} data-testid={`reject-kasbon-${idx}`}>Reject</Button>
                        {editingId === c.id && (
                          <Button variant="ghost" size="sm" className="text-muted-foreground h-8" onClick={() => setEditingId(null)}>Batal</Button>
                        )}
                      </>
                    ) : c.status === "lunas" ? (
                      <span className="text-xs text-muted-foreground px-2">Selesai</span>
                    ) : (
                      <>
                        <span className={`text-sm font-medium ${c.status === "approved" ? "text-[hsl(var(--success))]" : "text-destructive"}`} data-testid={`kasbon-decision-${idx}`}>
                          {c.status === "approved" ? "Approved" : "Rejected"}
                        </span>
                        <Button variant="ghost" size="sm" className="text-muted-foreground h-8" disabled={locked} title={locked ? "Periode sudah ditutup" : undefined} onClick={() => setEditingId(c.id)} data-testid={`edit-kasbon-${idx}`}>Edit</Button>
                      </>
                    )}
                    <DeleteConfirmButton
                      title="Hapus cash advance?"
                      description="Pengajuan cash advance dan jadwal cicilannya akan dihapus permanen."
                      onConfirm={() => remove(c.id)}
                      triggerTestId={`delete-admin-cashadvance-${idx}`}
                      confirmTestId={`confirm-delete-admin-cashadvance-${idx}`}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-muted-foreground mt-3">Klik baris untuk melihat detail & jadwal cicilan.</p>
      <KasbonDetailModal item={detail} onClose={() => setDetail(null)} canManage onToggle={toggleInstallment} lockedPeriods={closedPeriods} />
    </div>
  );
}
